import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";

type Align = "left" | "center";

interface MathProps {
  text: string;
  style?: React.CSSProperties;
  blockAlign?: Align;
}

type SegmentType = "text" | "inline" | "block";

interface Segment {
  type: SegmentType;
  value: string;
}

const INLINE_DOLLAR = /\$[^$\n]+\$/;
const INLINE_PAREN = /\\\([^)]*\\\)/;
const BLOCK_DOLLAR = /\$\$[\s\S]+?\$\$/;
const BLOCK_BRACKET = /\\\[[\s\S]+?\\\]/;

const ALL =
  /(\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]|\$[^$\n]+\$|\\\([^)]*\\\))/g;

function normalize(input: string): string {
  let out = input;

  out = out.replace(/\\\\/g, "\\");
  out = out.replace(/\\\(/g, "\\(");
  out = out.replace(/\\\)/g, "\\)");
  out = out.replace(/\\\[/g, "\\[");
  out = out.replace(/\\\]/g, "\\]");
  out = out.replace(/\r\n/g, "\n");
  out = out.replace(/\r/g, "\n");

  return out;
}

function cleanSpacing(text: string): string {
  return text
    .replace(/\s{2,}/g, " ")
    .replace(/\n+/g, " ")
    .trim();
}


function repairBrokenMath(input: string): string {
  return input.replace(
    /(\\\(|\$)([\s\S]*?)(\\\)|\$)/g,
    (_, open, content, close) => {
      let c = content;

      c = c.replace(/\s*\n\s*/g, " ");
      c = c.replace(/([a-zA-Z])\s*⃗/g, "\\\\vec{$1}");
c = c.replace(/⃗\s*([a-zA-Z])/g, "\\\\vec{$1}");
c = c.replace(/\\hat\s*\{\s*([ijk])\s*\}/g, "\\\\hat{$1}");
      c = c.replace(/\s*⋅\s*/g, " \\\\cdot ");
      c = c.replace(/\s*=\s*/g, " = ");

      return `${open}${c.trim()}${close}`;
    }
  );
}


function isInlineDollar(v: string): boolean {
  return v.startsWith("$") && v.endsWith("$") && !v.startsWith("$$");
}

function isInlineParen(v: string): boolean {
  return v.startsWith("\\(") && v.endsWith("\\)");
}

function isBlockDollar(v: string): boolean {
  return v.startsWith("$$") && v.endsWith("$$");
}

function isBlockBracket(v: string): boolean {
  return v.startsWith("\\[") && v.endsWith("\\]");
}

function stripInlineDollar(v: string): string {
  return v.slice(1, -1).trim();
}

function stripInlineParen(v: string): string {
  return v.slice(2, -2).trim();
}

function stripBlockDollar(v: string): string {
  return v.slice(2, -2).trim();
}

function stripBlockBracket(v: string): string {
  return v.slice(2, -2).trim();
}

function detectType(v: string): SegmentType {
  if (isBlockDollar(v)) return "block";
  if (isBlockBracket(v)) return "block";
  if (isInlineDollar(v)) return "inline";
  if (isInlineParen(v)) return "inline";
  return "text";
}

function stripValue(v: string): string {
  if (isBlockDollar(v)) return stripBlockDollar(v);
  if (isBlockBracket(v)) return stripBlockBracket(v);
  if (isInlineDollar(v)) return stripInlineDollar(v);
  if (isInlineParen(v)) return stripInlineParen(v);
  return v;
}

function splitSegments(input: string): Segment[] {
  const parts = input.split(ALL).filter(Boolean);
  const segments: Segment[] = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    const t = detectType(p);
    if (t === "text") {
      segments.push({ type: "text", value: p });
    } else {
      const val = stripValue(p);
      if (val.length === 0) {
        segments.push({ type: "text", value: p });
      } else {
        segments.push({ type: t, value: val });
      }
    }
  }
  return segments;
}

function renderInline(
  value: string,
  key: number
): React.ReactElement {
  return <InlineMath key={key} math={value} />;
}

function renderBlock(
  value: string,
  key: number,
  align: Align
): React.ReactElement {
  return (
    <div
      key={key}
      style={{
        display: "block",
        width: "100%",
        margin: "12px 0",
        textAlign: align,
      }}
    >
      <BlockMath math={value} />
    </div>
  );
}

function renderText(
  value: string,
  key: number
): React.ReactElement {
  return (
    <span
      key={key}
      style={{
        whiteSpace: "pre-wrap",
      }}
    >
      {value}
    </span>
  );
}

function sanitizeStyle(
  style?: React.CSSProperties
): React.CSSProperties {
  return {
    color: "#000",
    fontWeight: 400,
    fontStyle: "normal",
    textDecoration: "none",
    ...style,
  };
}

function renderSegments(
  segments: Segment[],
  align: Align
): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (seg.type === "inline") {
      nodes.push(renderInline(seg.value, i));
    } else if (seg.type === "block") {
      nodes.push(renderBlock(seg.value, i, align));
    } else {
      nodes.push(renderText(seg.value, i));
    }
  }
  return nodes;
}

function safeText(text: string): string {
  if (text === null || text === undefined) return "";
  if (typeof text !== "string") return String(text);
  return text;
}

function normalizeWhitespace(text: string): string {
  return text.replace(/\u00a0/g, " ");
}

function prepare(text: string): string {
  let t = safeText(text);
  t = normalizeWhitespace(t);
  t = normalize(t);
  t = repairBrokenMath(t);
    t = cleanSpacing(t);

  return t;
}


function hasMath(text: string): boolean {
  return (
    INLINE_DOLLAR.test(text) ||
    INLINE_PAREN.test(text) ||
    BLOCK_DOLLAR.test(text) ||
    BLOCK_BRACKET.test(text)
  );
}

function fallbackRender(text: string): React.ReactNode {
  return (
    <span
      style={{
        whiteSpace: "pre-wrap",
      }}
    >
      {text}
    </span>
  );
}

function renderAll(
  text: string,
  align: Align
): React.ReactNode {
  if (!hasMath(text)) {
    return fallbackRender(text);
  }
  const segments = splitSegments(text);
  return renderSegments(segments, align);
}

const MathText: React.FC<MathProps> = ({
  text,
  style,
  blockAlign = "center",
}) => {
  if (!text) return null;

  const prepared = prepare(text);
  const finalStyle = sanitizeStyle(style);

  return (
    <span
      style={{
        ...finalStyle,
        lineHeight: 1.6,
        whiteSpace: "pre-wrap",
      }}
    >
      {renderAll(prepared, blockAlign)}
    </span>
  );
};

export default MathText;
